import { useState } from "react";

import { MockHorizontalCard } from "@/__mocks__/ui";
import {
  AnalyticsFieldName,
  GroupClassSeriesAnalyticsEvent,
  GroupClassSeriesAnalyticsSources,
} from "@/constants/analytics/Analytics";
import { ModalPath } from "@/constants/Navigation";
import { ExploreTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import useFuseSearch from "@/hooks/useFuseSearch";
import { useAnalytics } from "@/services/Analytics";
import { useGroupClassDetailsModalDispatch } from "@/store/reducers/modals/groupClassDetailsModal";
import type { GroupClassCardItem } from "@/ui/components/groupClasses/series/GroupClassCard";
import GroupClassSeriesCard from "@/ui/components/groupClasses/series/GroupClassSeriesCard";
import GroupClassSeriesListItem from "@/ui/components/groupClasses/series/GroupClassSeriesListItem";
import { FAIconName, TextField } from "@/ui/core";
import { Box, FlatList, KeyboardAvoidingView, VStack } from "@/ui/layout";
import { LayoutToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

interface GroupClassSeriesListProps {
  forWhom?: string;
  // flag on: normalized card view-models; when set, renders GroupClassCard
  groupCardItems?: GroupClassCardItem[];
  groupClasses: GroupClassSeries[];
  isAppUserSupport: boolean;
  isListLoading: boolean;
  onRefresh: () => void;
  patientId: string;
  shouldHideEmptyComponent: boolean;
}

// a normalized v2 card view-model carries `id` (the groupId); a v1 series does not
const isGroupCardItem = (
  item: GroupClassCardItem | GroupClassSeries,
): item is GroupClassCardItem => "id" in item;

// a v2 card is keyed by its section id (a user can hold multiple sections of the same
// group, so groupId alone isn't unique), falling back to groupId for non-registered cards;
// a v1 series is keyed by its external id
const getListItemKey = (item: GroupClassCardItem | GroupClassSeries): string =>
  isGroupCardItem(item)
    ? (item.groupSectionId ?? item.id)
    : item.groupClassExternalId;

const GroupClassSeriesList = ({
  forWhom,
  groupCardItems,
  groupClasses: allGroupClasses,
  isAppUserSupport,
  isListLoading,
  onRefresh,
  patientId,
  shouldHideEmptyComponent,
}: GroupClassSeriesListProps) => {
  const { track } = useAnalytics();

  const { setGroupClassDetailsData } = useGroupClassDetailsModalDispatch();

  const [searchValue, setSearchValue] = useState<string>("");
  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  const groupClasses = useFuseSearch<GroupClassSeries>({
    data: allGroupClasses,
    searchKeys: ["title", "description", "recurrenceDisplayString"],
    searchValue,
  });

  const filteredGroupCardItems = useFuseSearch<GroupClassCardItem>({
    data: groupCardItems ?? [],
    searchKeys: ["title", "description"],
    searchValue,
  });

  const handleGroupClassCardPress = ({
    externalId,
    groupSectionId,
    title,
  }: {
    externalId: string;
    groupSectionId?: string;
    title: string;
  }) => {
    track(GroupClassSeriesAnalyticsEvent.CardPressed, {
      [AnalyticsFieldName.Component]: GroupClassSeriesCard.name,
      [AnalyticsFieldName.GroupClassId]: externalId,
      [AnalyticsFieldName.PatientId]: patientId,
      [AnalyticsFieldName.Source]: GroupClassSeriesList.name,
      [AnalyticsFieldName.Title]: title,
    });

    setGroupClassDetailsData({
      forWhom,
      groupClassExternalId: externalId,
      groupSectionId,
      isAppUserSupport,
      isDisabled: isListLoading,
      patientId,
      source: GroupClassSeriesAnalyticsSources.SearchList,
    });
    router.navigate(ModalPath.GroupClassListDetails);
  };

  const emptyListComponent = () => (
    <MockHorizontalCard
      id={joinTestIDs(
        ExploreTestID.GroupClassSeriesList,
        ExploreTestID.EmptyList,
        ExploreTestID.MockCard,
      )}
      maxWidth="100%"
    />
  );

  // single list, conditional data + card: group card items when the flag is on, else v1
  const listData: (GroupClassCardItem | GroupClassSeries)[] = groupCardItems
    ? filteredGroupCardItems
    : groupClasses;

  return (
    <KeyboardAvoidingView keyboardVerticalOffset={0} style={{ flex: 1 }}>
      <VStack flex={1} gap={24} paddingTop={24}>
        <Box paddingHorizontal={LayoutToken.ContentPaddingHorizontal}>
          <TextField
            accessibilityLabel="Search"
            autoCapitalize="sentences"
            hasClearButton
            icon={FAIconName.Search}
            isAutoCorrect={false}
            onChange={handleSearchChange}
            placeholder="Search groups"
            testID={ExploreTestID.GroupClasses}
            value={searchValue}
          />
        </Box>
        <FlatList
          data={listData}
          emptyListComponent={emptyListComponent}
          isDataLoading={shouldHideEmptyComponent}
          isRefreshControlLoading={isListLoading}
          isScrollEnabled={!isListLoading}
          keyExtractor={getListItemKey}
          onRefresh={onRefresh}
          renderItem={({ item }) => (
            <GroupClassSeriesListItem
              forWhom={forWhom}
              isAppUserSupport={isAppUserSupport}
              isListLoading={isListLoading}
              item={item}
              onSelect={handleGroupClassCardPress}
              patientId={patientId}
            />
          )}
          // search resizes the list; anchoring would push content down and leave a blank gap
          shouldDisableMaintainVisibleContentPosition
          shouldRemoveContentVerticalPadding
          shouldShowRefreshControl
          testID={ExploreTestID.GroupClasses}
        />
      </VStack>
    </KeyboardAvoidingView>
  );
};

export type { GroupClassSeriesListProps };
export default GroupClassSeriesList;
